// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';
import './WarpControls.css';
import { getDifficultyMultipliers } from '../utils/DifficultyUtils';

/**
 * WarpControls Component
 * Warp factor selection with slider and navigation mode controls
 */
const WarpControls = ({
  selectedWarpFactor,
  navigationMode,
  onWarpSelection,
  onModeChange,
  getDefaultWarpFactor
}) => {
  
  const effectiveWarp = selectedWarpFactor || (navigationMode !== 'MANUAL' ? getDefaultWarpFactor() : 1);
  
  const getWarpColor = (factor) => {
    if (factor <= 3) return '#00ff88'; // Green - safe
    if (factor <= 6) return '#ffaa00'; // Orange - caution
    if (factor <= 7) return '#ff6600'; // Red-orange - dangerous
    return '#ff4444'; // Red - critical
  };

  const getWarpLabel = (factor) => {
    if (factor >= 9) return 'CRITICAL';
    if (factor >= 8) return 'DANGER';
    if (factor >= 7) return 'HIGH RISK';
    if (factor >= 5) return 'CAUTION';
    return 'SAFE';
  };

  return (
    <div className="warp-controls-container">
      {/* Warp Factor Slider */}
      <div className="warp-slider-section">
        <div className="warp-header">
          <span className="warp-label">WARP FACTOR</span>
          <span 
            className="warp-value"
            style={{ color: getWarpColor(effectiveWarp) }}
          >
            {effectiveWarp}
          </span>
        </div>
        
        <div className="warp-slider-container">
          <input
            type="range"
            min="1"
            max="9"
            step="1"
            value={effectiveWarp}
            onChange={(e) => onWarpSelection(parseInt(e.target.value))}
            className="warp-slider"
            style={{
              background: `linear-gradient(to right, 
                #00ff88 0%, 
                #00ff88 ${((effectiveWarp - 1) / 8) * 33}%, 
                #ffaa00 ${((effectiveWarp - 1) / 8) * 33}%, 
                #ffaa00 ${((effectiveWarp - 1) / 8) * 66}%, 
                #ff4444 ${((effectiveWarp - 1) / 8) * 66}%, 
                #ff4444 ${((effectiveWarp - 1) / 8) * 100}%, 
                rgba(0, 255, 255, 0.1) ${((effectiveWarp - 1) / 8) * 100}%)`
            }}
          />
          
          <div className="warp-ticks">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(tick => (
              <span 
                key={tick} 
                className="warp-tick"
                style={{ 
                  color: tick <= effectiveWarp ? getWarpColor(tick) : '#444',
                  fontWeight: tick === effectiveWarp ? 'bold' : 'normal'
                }}
              >
                {tick}
              </span>
            ))}
          </div>
        </div>
        
        <div 
          className="warp-status"
          style={{ color: getWarpColor(effectiveWarp) }}
        >
          {getWarpLabel(effectiveWarp)}
          {effectiveWarp >= 8 && ' ⚠️'}
          {effectiveWarp >= 9 && ' 💥'}
        </div>
        
        {effectiveWarp >= 8 && (
          <div className="warp-warning">
            {effectiveWarp === 8 && `⚠️ ${Math.round(getDifficultyMultipliers().warp8DamageChance * 100)}% ENGINE DAMAGE RISK`}
            {effectiveWarp === 9 && `💥 ${Math.round(getDifficultyMultipliers().warp9DamageChance * 100)}% CRITICAL DAMAGE RISK`}
          </div>
        )}
      </div>

      {/* Navigation Mode Buttons */}
      <div className="mode-section">
        <div className="mode-label">NAVIGATION MODE</div>
        <div className="mode-buttons">
          <button 
            className={`mode-button ${navigationMode === 'MANUAL' ? 'active' : ''}`}
            onClick={() => onModeChange('MANUAL')}
          >
            MANUAL
          </button>
          <button 
            className={`mode-button ${navigationMode === 'AUTO' ? 'active' : ''}`}
            onClick={() => onModeChange('AUTO')}
          >
            AUTO
          </button>
          <button 
            className={`mode-button emergency ${navigationMode === 'EMERGENCY' ? 'active' : ''}`}
            onClick={() => onModeChange('EMERGENCY')}
          >
            EMERGENCY
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarpControls;
